import { type FC, Fragment, type ReactElement, type SyntheticEvent, useEffect, useState } from 'react';
import Select from 'react-select';
import { FileUpload, Modal } from '@/components';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { tr } from 'date-fns/locale/tr';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { useApi } from '@/api/useApi.tsx';
import { useFileUpload } from '@/utility/use-util.tsx';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { EffectCoverflow } from 'swiper/modules';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';

import type {
	CompetencyResponse,
	MissionCompositionCreateRequest,
	MissionContentResponse,
	MissionTemplateResponse,
	ResponseDTOListMissionTemplateResponse,
	ResponseDTOMissionCompositionResponse,
	ResponseDTOMissionContentResponse
} from '@dopingtech/live_expert_bff_api_ts/api.ts';
import type { AxiosResponse } from 'axios';
import type { GroupedOption, Options, UploadedFile } from '@/components/student-missions/student-mission.types.ts';

import ClipboardIcon from '@/assets/svg/assignments-clipboard.svg';
import CloudFolderIcon from '@/assets/images/cloud-folder.png';
import CheckIcon from '@/assets/images/check-green.png';
import LoadingSpinner from '@/components/loading-spinner';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { useSizes } from '@/utility/responsive.ts';
import clsx from 'clsx';
import { TimeSpan } from '@/utility/time-span.ts';
import { isWebView } from '@/utility/webview.ts';

type Props = {
	onChangeAnyField?: (isChange: boolean) => void;
	onComplete?: () => void;
	studentId: number;
	gradeId: number;
	competenciesList?: CompetencyResponse[];
};

const LimitedUploadList: number[] = Array.from(Array(4).keys());

export const MissionActions: FC<Props> = ({ onChangeAnyField, studentId, gradeId, onComplete, competenciesList }: Props): ReactElement<Props> => {
	const { MissionControllerApi } = useApi();
	const { uploadFile } = useFileUpload();
	const { isMobile } = useSizes();

	registerLocale('tr', tr);
	setDefaultLocale('tr');

	const [fileContent, setFileContent] = useState<MissionContentResponse[]>([]);
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [activePage, setActivePage] = useState<number>(0);
	const [dueDate, setDueDate] = useState<Date>(dayjs().add(1, 'day').toDate());
	const [competencies, setCompetencies] = useState<GroupedOption[]>([{ label: '', options: [] }]);
	const [selectedCompetencies, setSelectedCompetencies] = useState<Options[]>([]);
	const [missionName, setMissionName] = useState<string>('');
	const [missionDescription, setMissionDescription] = useState<string>('');
	const [swiper, setSwiper] = useState<SwiperCore | null>(null);
	const [swiperActiveIndex, setSwiperActiveIndex] = useState<number>(0);
	const [openPreview, setOpenPreview] = useState<number>(-1);
	const [openSuccessful, setOpenSuccessful] = useState<boolean>(false);
	const [openDelete, setOpenDelete] = useState<number>(-1);
	const [showPreloader, setShowPreloader] = useState<boolean>(false);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

	const { data: templates } = useQueryProvider<
		AxiosResponse<ResponseDTOListMissionTemplateResponse>,
		Error,
		MissionTemplateResponse[]
	>(
		[QUERY_KEYS.getMissionTemplates, gradeId, selectedCompetencies.map(c => c.value)],
		() => MissionControllerApi.getMissionTemplates(
			gradeId,
			selectedCompetencies.map(c => String(c.value))
		) as any,
		{
			select: ({ data }) => data?.data ?? [],
			enabled: selectedCompetencies.length > 0,
			refetchOnWindowFocus: false
		}
	);

	const documentsByContentTypeMutation = useMutationProvider<AxiosResponse<ResponseDTOMissionContentResponse>, Error, string>(type => MissionControllerApi.getUploadUrl1(type) as any);

	const createMissionMutation = useMutationProvider<AxiosResponse<ResponseDTOMissionCompositionResponse>, Error, MissionCompositionCreateRequest>(
		payload => MissionControllerApi.createMissionComposition(payload) as any
	);

	const documentUpload = async (fileObj: File | null): Promise<void> => {
		if (fileObj) {
			setShowPreloader(true);

			const result = await documentsByContentTypeMutation.mutateAsync(fileObj?.type).catch(error => error);

			if (result?.data?.data) {
				const uploadResult: any = await uploadFile(fileObj, result?.data?.data?.url);

				if (uploadResult instanceof Error || !uploadResult?.ok) {
					toast.error(uploadResult?.message || uploadResult?.statusText || 'Dosya yükleme başarısız oldu.');
				} else {
					toast.success('Dosya başarıyla yüklendi.');

					setFileContent(value => [...value, result?.data?.data]);
					setUploadedFiles(payload => [...payload, { file: fileObj, name: fileObj?.name, previewUrl: `${process.env.EXPERT_CDN}/${result?.data?.data?.fileName}` }]);
				}
			}

			setShowPreloader(false);
		}
	};

	const onRemoveFile = (): void => {
		if (openDelete >= 0) {
			setFileContent(prev => prev.filter((_, i) => i !== openDelete));
			setUploadedFiles(prev => prev.filter((_, i) => i !== openDelete));
			setOpenDelete(-1);
		}
	};

	const onNext = (): void => {
		setActivePage(value => ++value);
	};

	const onPrev = (): void => {
		setActivePage(value => {
			const newPage = --value;

			return newPage < 0 ? 0 : newPage;
		});
	};

	const onFinish = async (): Promise<void> => {
		const payload = {
			studentId: studentId,
			gradeId: gradeId || 0,
			missionName,
			missionDescription,
			dueDate: dayjs(dueDate).format('YYYY-MM-DDTHH:mm:ssZ'),
			competency: selectedCompetencies.map(i => i.value).join(','),
			fileNames: fileContent.map(i => i.fileName)
		};

		const result = await createMissionMutation.mutateAsync(payload as MissionCompositionCreateRequest).catch(error => error);

		if (result instanceof Error) {
			toast.error(result.message || 'Görev oluşturulamadı. Lütfen tekrar deneyin.');
		} else {
			setOpenSuccessful(true);
			setActivePage(0);
			setDueDate(new Date());
			setSelectedCompetencies([]);
			setMissionName('');
			setMissionDescription('');
			setFileContent([]);
			setUploadedFiles([]);
		}
	};

	const onSuccess = (): void => {
		setOpenSuccessful(false);
		onComplete?.();
	};

	const onSelectTemplate = (template: MissionTemplateResponse): void => {
		setSelectedTemplateId(template.id ?? null);
		setMissionName(template.name ?? '');
		setMissionDescription(template.description ?? '');
		const attachments = template.attachmentList ?? [];
		setFileContent(attachments.map(att => ({ url: att.url, fileName: att.fileName })));
		setUploadedFiles(attachments.map(att => ({
			file: null,
			name: att.fileName ?? '',
			previewUrl: att.url ?? ''
		})));
	};

	const onMobileFocus = (event: SyntheticEvent): void => {
		if (isWebView()) {
			const element = event?.target as HTMLElement;

			if (element) {
				setTimeout(() => {
					element.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}, TimeSpan.milliSecond.sevenHundred);
			}
		}
	};

	useEffect(() => {
		if (competenciesList?.length) {
			setCompetencies([
				{
					label: 'Birden fazla yetkinlik seçimi yapabilirsiniz',
					options: [...competenciesList.map(i => ({ value: i.id!, label: i.name! }))]
				}
			]);
		}
	}, [competenciesList]);

	useEffect(() => {
		if (openPreview !== -1 && swiper) {
			swiper.slideTo(openPreview);
			setSwiperActiveIndex(openPreview);
		}
	}, [openPreview, swiper]);

	useEffect(() => {
		onChangeAnyField?.(!!(uploadedFiles.length || selectedCompetencies.length || missionName || missionDescription));
	}, [uploadedFiles, selectedCompetencies, missionName, missionDescription, onChangeAnyField]);

	return (
		<Fragment>
			<div className={clsx('mission-form-container', { success: openSuccessful })}>
				<Choose>
					<When condition={activePage === 1}>
						<div className="page-two">
							<h2 className="mission-form-header">Teslim Tarihi Seçimi</h2>
							<p className="mission-form-header-description">Öğrencinizin görevi tamamlaması gereken teslim tarihini belirleyin.</p>
							<div className="picker-container">
								<DatePicker
									locale="tr"
									showPopperArrow={false}
									minDate={dayjs().add(1, 'day').toDate()}
									inline={true}
									selected={dueDate}
									onChange={setDueDate as any}
								/>
							</div>
						</div>
						<div className="mission-form-footer">
							<button
								className="btn btn-secondary"
								type="button"
								onClick={onPrev}>
								<i className="icons-arrow-left" />
								Geri
							</button>
							<button
								className="btn"
								type="button"
								onClick={onNext}>
								Devam Et
								<i className="icons-arrow-right" />
							</button>
						</div>
					</When>
					<When condition={activePage === 2}>
						<div className="page-three">
							<h2 className="mission-form-header">Görev Önizleme</h2>
							<p className="mission-form-header-description">Görevi göndermeden önce son kontrollerinizi yapabilirsiniz. Öğrenciniz ödevi bu şekilde görecektir.</p>
							<div className="preview-detail-wrapper">
								<div className="preview-detail">
									<span className="preview-detail-label">Görev Adı</span>
									<span className="preview-detail-value">{missionName}</span>
								</div>
								<div className="preview-detail">
									<span className="preview-detail-label">Son Teslim Tarihi</span>
									<span className="preview-detail-value">
										<i className="icons-calendar" />
										{dayjs(dueDate).locale('tr')?.format('DD MMMM, dddd')}
									</span>
								</div>
							</div>
							<div className="preview-description-wrapper">
								<img
									alt=""
									className="description-icon"
									src={ClipboardIcon}
								/>
								<div className="preview-description">
									<span className="preview-description-label">Görev Detayları</span>
									<p className="preview-description-value">{missionDescription}</p>
								</div>
							</div>
							<If condition={!!uploadedFiles?.length}>
								<div className="preview-attachment-wrapper">
									<img
										alt=""
										className="description-icon"
										src={CloudFolderIcon}
									/>
									<div className="preview-description">
										<span className="preview-description-label">Görev Ekleri</span>
										<div className="upload-container">
											{uploadedFiles.map((item, index) => (
												<FileUpload
													key={index}
													containerClassName="mission-file-upload-preview"
													currentFile={item as any}
													onShowPreview={() => setOpenPreview(index)}
													previewMode={true}
													iconThumb={true}
												/>
											))}
										</div>
									</div>
								</div>
							</If>
						</div>
						<div className="mission-form-footer">
							<button
								className="btn btn-secondary"
								type="button"
								onClick={onPrev}>
								<i className="icons-arrow-left" />
								Geri
							</button>
							<button
								className="btn"
								type="button"
								onClick={onFinish}>
								Görevi Gönder
								<i className="icons-arrow-right" />
							</button>
						</div>
					</When>
					<Otherwise>
						<div className="page-one">
							<h2 className="mission-form-header">Görev Oluştur</h2>
							<p className="mission-form-header-description">
								Öğrencinizin hangi yetkinliğini geliştirmek istediğinizi WAY CASEL Becerileri'nden seçin ve görevle ilişkili alanları doldurun.
							</p>
							<div className="form">
								<div className="form-box">
									<span className="form-label">WAY CASEL Becerileri</span>
									<Select
										value={selectedCompetencies}
										isMulti={true}
										placeholder="Yetkinlik Seçiniz"
										options={competencies}
										className="multi-select"
										classNamePrefix="select"
										isSearchable={false}
										onChange={payload => {
											setSelectedCompetencies(payload as Options[]);
											setSelectedTemplateId(null);
										}}
									/>
								</div>
								<If condition={(selectedCompetencies.length > 0) && !!(templates?.length)}>
									<div className="form-box">
										<span className="form-label">Hazır Görevler</span>
										<Select
											value={selectedTemplateId ? { value: selectedTemplateId, label: templates?.find(t => t.id === selectedTemplateId)?.name ?? '' } : null}
											placeholder="Hazır görev seçiniz"
											options={templates?.map(t => ({ value: t.id!, label: t.name! })) ?? []}
											className="single-select"
											classNamePrefix="select"
											isSearchable={false}
											onChange={option => {
												const template = templates?.find(t => t.id === option?.value);
												if (template) onSelectTemplate(template);
											}}
										/>
									</div>
								</If>
								<div className="form-box">
									<span className="form-label">Görev Adı</span>
									<input
										type="text"
										className="textbox"
										maxLength={35}
										placeholder="Oluşturmak istediğiniz göreve bir isim verin"
										defaultValue={missionName}
										onFocus={onMobileFocus}
										onChange={e => setMissionName(e.target.value)}
									/>
								</div>
								<div className="form-box">
									<span className="form-label">Görev Yönergeleri</span>
									<textarea
										maxLength={500}
										className="textarea"
										placeholder="Oluşturmak istediğiniz görevin yönergelerini ve öğrenciden beklediğiniz detayları yazın."
										defaultValue={missionDescription}
										onFocus={onMobileFocus}
										onChange={e => setMissionDescription(e.target.value)}
									/>
								</div>
								<div className="form-box">
									<span className="form-label">Görev Ekleri</span>
									<div className="upload-container">
										{LimitedUploadList.map((_, index) => (
											<FileUpload
												key={index}
												containerClassName="mission-file-upload"
												accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/*"
												onChange={documentUpload}
												iconThumb={true}
												currentFile={uploadedFiles[index] as any}
												onShowPreview={() => setOpenPreview(index)}
												onRemove={() => setOpenDelete(index)}
											/>
										))}
									</div>
								</div>
							</div>
						</div>
						<div className="mission-form-footer">
							<div className="empty" />
							<button
								disabled={!(missionName && missionDescription && selectedCompetencies?.length)}
								className="btn"
								type="button"
								onClick={onNext}>
								Devam Et
								<i className="icons-arrow-right" />
							</button>
						</div>
					</Otherwise>
				</Choose>
				<If condition={showPreloader || createMissionMutation.isLoading}>
					<LoadingSpinner loadingText={'Yükleniyor'} />
				</If>
			</div>
			<Modal
				isOpen={openPreview !== -1}
				onClose={() => setOpenPreview(-1)}
				closeOnEsc={true}
				closeOnClickOutside={false}
				size={isMobile ? 'full' : 'xl'}
				className="mission-preview-modal">
				<div className="mission-modal-document-preview">
					<a
						className="close-button"
						onClick={() => setOpenPreview(-1)}>
						<i className="icons-close-circle" />
					</a>
					<div className="document-preview-container">
						<Swiper
							key={`swiper-${isMobile ? 'mobile' : 'desktop'}`}
							onSwiper={setSwiper}
							allowTouchMove={false}
							preventClicks={false}
							simulateTouch={false}
							touchStartPreventDefault={false}
							preventClicksPropagation={false}
							slidesPerView={1.5}
							centeredSlides={true}
							onActiveIndexChange={swiperInstance => setSwiperActiveIndex(swiperInstance.activeIndex)}
							effect="coverflow"
							coverflowEffect={{
								rotate: 0,
								scale: isMobile ? 1 : 0.6,
								stretch: 1,
								modifier: 1,
								slideShadows: false
							}}
							modules={[EffectCoverflow]}
							spaceBetween={isMobile ? 35 : -90}
							className="appointment-swiper swiper-no-swiping">
							{uploadedFiles.map((item, index) => (
								<SwiperSlide key={index}>
									<FileUpload
										key={index}
										containerClassName="mission-file-upload-preview"
										currentFile={item as any}
										previewMode={true}
										isDownload={false}
									/>
								</SwiperSlide>
							))}
						</Swiper>
						<div className="swiper-navigate-buttons">
							<button
								disabled={!swiperActiveIndex}
								className="btn"
								type="button"
								onClick={() => swiper?.slidePrev()}>
								<i className="icons-arrow-left" />
								Önceki
							</button>
							<button
								disabled={swiperActiveIndex === uploadedFiles.length - 1}
								className="btn"
								type="button"
								onClick={() => swiper?.slideNext()}>
								Sonraki
								<i className="icons-arrow-right" />
							</button>
						</div>
					</div>
				</div>
			</Modal>
			<Modal
				isOpen={openSuccessful}
				onClose={() => setOpenSuccessful(false)}
				closeOnEsc={true}
				closeOnClickOutside={false}
				size="sm"
				className="mission-create-successful-modal">
				<div className="mission-modal-content">
					<a
						className="close-button"
						onClick={onSuccess}>
						<i className="icons-close" />
					</a>
					<div className="mission-modal-wrapper">
						<img
							alt=""
							src={CheckIcon}
							className="info-image"
						/>
						<span className="info-header">Tebrikler!</span>
						<span className="info-description">Göreviniz oluşturuldu ve öğrencinize atandı.</span>
						<a
							className="info-blue-btn"
							onClick={onSuccess}>
							Kapat
						</a>
					</div>
				</div>
			</Modal>
			<Modal
				isOpen={openDelete >= 0}
				onClose={() => setOpenDelete(-1)}
				closeOnEsc={true}
				closeOnClickOutside={false}
				size="sm"
				className="mission-delete-modal">
				<div className="mission-modal-content">
					<a
						className="close-button"
						onClick={() => setOpenDelete(-1)}>
						<i className="icons-close" />
					</a>
					<div className="mission-modal-wrapper">
						<i className="icons-trash-bin-minimalistic" />
						<span className="info-header">Belgeyi Sil</span>
						<span className="info-description">Bu işlemi geri alamazsınız. Devam etmek istediğinize emin misiniz?</span>
						<div className="buttons-wrapper">
							<a
								className="info-grey-btn"
								onClick={() => setOpenDelete(-1)}>
								Vazgeç
							</a>
							<a
								className="info-red-btn"
								onClick={onRemoveFile}>
								Sil
							</a>
						</div>
					</div>
				</div>
			</Modal>
		</Fragment>
	);
};
